import {useEffect, useState} from 'react';

import {merchantService} from '@/services/merchants';
import type {BusinessPaymentMethod} from '@/types/AfriexBusinessPaymentMethod';
import {getResponseMessage, getThrownMessage} from '@/utils/errorMessage';
import {logRejection} from '@/utils/logRejection';
import {showErrorMessage} from '@/utils/showErrorMessage';

export const useBusinessVirtualAccounts = (businessId: string) => {
  const [virtualAccounts, setVirtualAccounts] = useState<
    BusinessPaymentMethod[]
  >([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!businessId) {
      return;
    }

    const controller = new AbortController();
    const {signal} = controller;

    const fetch = async (): Promise<void> => {
      setIsLoading(true);
      try {
        const accounts = await merchantService.getBusinessVirtualAccounts(
          businessId,
          signal,
        );
        if (signal.aborted) {
          return;
        }
        setVirtualAccounts(accounts ?? []);
      } catch (e) {
        // The request was superseded (businessId changed or the screen
        // unmounted); a newer request now owns the state, so ignore this one.
        if (signal.aborted) {
          return;
        }
        showErrorMessage(
          `Failed to load virtual accounts: ${
            getResponseMessage(e) ?? getThrownMessage(e) ?? String(e)
          }`,
        );
        setVirtualAccounts([]);
      } finally {
        if (!signal.aborted) {
          setIsLoading(false);
        }
      }
    };

    // `fetch` toasts its own failures, so this only fires if that catch or the
    // finally itself throws. Log rather than swallow silently.
    fetch().catch(logRejection('Failed to load business virtual accounts'));
    return () => controller.abort();
  }, [businessId]);

  return {virtualAccounts, isLoading};
};
